import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaExchangeAlt, FaPrint } from 'react-icons/fa';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionActions,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { DataTable } from '../../styles/ui/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import { EntityActionBar, ActionBarSpacer } from '../../styles/ui/ActionBar';
import { CtaButton, DangerButton, SmallButton } from '../../styles/ui/Button';
import { TextLink } from '../../styles/ui/Link';

import Loading from '../../components/Loading';
import DeleteMovementModal from '../../components/DeleteMovementModal';
import StatusBadge from '../../components/StatusBadge';
import usePermissions from '../../components/Menu/MenuItems/hooks/usePermissions';
import axios from '../../services/axios';
import {
   deleteMovement,
   getMovement,
   readMovementApiError,
} from '../../services/movements/movementsApi';
import { notifyStockWarnings } from '../../utils/movementFeedback';
import {
   formatMovementDateTime,
   movementFlagLabel,
   movementFlagVariant,
   movementPdvLabel,
   movementUserLabel,
   moveTypeLabel,
   moveTypeVariant,
} from '../../utils/movementFormat';

import {
   DataGrid,
   DataItem,
   HeadPills,
   TotalRow,
   TransferNote,
} from './styled';

// ===========================================================================
// Detalhe do movimento (`GET /movements/:id`) — somente leitura, com as ações
// de excluir / imprimir / editar.
//
// Redesenho 2026-09-12: os `<input disabled>` viraram grade de leitura
// (legenda + valor), os itens passaram para a tabela do kit novo com total no
// rodapé, e o erro de carga deixou de ser `console.error` silencioso.
//
// Regras preservadas: guard de transferência (handoff 0099 §4.3), XML
// imutável (0093 §3), exclusão com motivo (0095) e "Voltar" por
// `navigate(-1)`, que restaura a listagem filtrada (navegacao.md 1.6).
//
// Fora do protótipo por falta de contrato: a timeline de "Histórico" (o C4
// grava `movement_events`, mas não há endpoint de leitura) e a coluna
// "Estoque após" por item (não existe saldo histórico por linha).
// ===========================================================================

const TRANSFER_ROLE_LABEL = {
   out: 'saída da origem',
   in: 'entrada no destino',
   reversion: 'reversão de remessa cancelada',
};

export default function Movement() {
   const [movement, setMovement] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [loadFailed, setLoadFailed] = useState(false);
   const [productDetails, setProductDetails] = useState([]);
   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   // Voltar preservando a listagem (navegacao.md 1.6): se há histórico nesta
   // sessão, navigate(-1) restaura a URL filtrada (filtros + página + sort) de
   // onde o usuário veio; senão (deep-link direto no detalhe), cai na lista.
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/movements');
   };

   const isConfirmed = movement?.movement_flag === 'confirmed';
   const isDeleted = movement?.movement_flag === 'deleted';

   // Vínculo com transferência entre PDVs (handoff 0099 §4.3, adendo
   // 2026-08-21): campo aditivo `transfer` nos reads de movimento. Quando não
   // é nulo, este movimento é o out/in/reversão de uma remessa e sua mutação
   // pertence ao fluxo de transferência — não a esta tela.
   const transferBinding = movement?.transfer ?? null;

   // Atalho para a ordem: só existe com o vínculo E com permissão de leitura de
   // transferências — sem a chave, o botão levaria a um /403.
   const showTransferShortcut =
      !!transferBinding && hasPermission('inventory:transfer:read');

   useEffect(() => {
      const fetchMovementData = async () => {
         setIsLoading(true);
         try {
            const movementData = await getMovement(id);
            setMovement(movementData);
            setLoadFailed(false);

            // N+1 conhecido do módulo: um GET /products/:id por item, pareado
            // por índice. Não há endpoint que devolva o item já com SKU/nome.
            if (movementData?.ItemMovements?.length) {
               const productResponses = await Promise.all(
                  movementData.ItemMovements.map((item) =>
                     axios.get(`/products/${item.prod_id}`)
                  )
               );
               setProductDetails(
                  productResponses.map(
                     (res) => res.data.data?.product ?? res.data // envelope + fallback
                  )
               );
            } else {
               setProductDetails([]);
            }
         } catch (error) {
            setMovement(null);
            setProductDetails([]);
            setLoadFailed(true);
            // Antes era `console.error` mudo: 403, 404, 500 e rede caíam todos
            // na mesma tela de "não encontrado", sem dizer o que houve.
            toast.error(readMovementApiError(error).message);
         } finally {
            setIsLoading(false);
         }
      };

      fetchMovementData();
   }, [id]);

   // Confirmação vem do DeleteMovementModal (handoff 0095): motivo
   // obrigatório, validado no modal antes do submit (o 422 é só rede de
   // segurança). Em falha o modal permanece aberto pro operador corrigir.
   const handleDeleteMovement = async (cancelledReason) => {
      try {
         setIsLoading(true);
         // JSend Geração 3 (handoff 0093): a exclusão cria um movimento
         // reverso; o `message` do backend já informa o ID dele.
         const { stockWarnings, message } = await deleteMovement(id, {
            cancelledReason,
         });
         toast.success(message);
         notifyStockWarnings(stockWarnings);
         setIsDeleteModalOpen(false);
         // Volta pra listagem filtrada (remonta e refetcha pela URL — o
         // movimento reaparece já marcado como EXCLUÍDO).
         handleBack();
      } catch (error) {
         const apiError = readMovementApiError(error);
         toast.error(apiError.fieldErrors.cancelled_reason ?? apiError.message);
      } finally {
         setIsLoading(false);
      }
   };

   // Impressão passou a ser do FRONT em 2026-09-13: o botão navega para a
   // pré-visualização do comprovante (`/movements/:id/receipt`), que imprime
   // pelo navegador. O `GET /movements/:id/pdf` (Puppeteer por request) não é
   // mais chamado por nenhuma tela — a rota segue no ar até uma sessão de
   // backend removê-la.
   const handlePrintMovement = () => navigate(`/movements/${id}/receipt`);

   // `useMemo` na própria lista: o fallback `?? []` cria um array novo a cada
   // render e faria o total recalcular sempre (e o lint reclama, com razão).
   const items = useMemo(
      () => movement?.ItemMovements ?? [],
      [movement?.ItemMovements]
   );

   const totalUnits = useMemo(
      () =>
         items.reduce((total, item) => total + (Number(item.prod_qtd) || 0), 0),
      [items]
   );

   // Subtítulo do header: PDV · documento · data · usuário.
   const subtitle = movement
      ? [
           movementPdvLabel(movement),
           [movement.doc_type, movement.doc_number && `nº ${movement.doc_number}`]
              .filter(Boolean)
              .join(' '),
           formatMovementDateTime(movement.created_at),
           movementUserLabel(movement),
        ]
           .filter(Boolean)
           .join(' · ')
      : 'Visualização do movimento e itens associados.';

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Movimento #{movement?.id ?? id}</PageTitle>
               <PageSubtitle>{subtitle}</PageSubtitle>
            </PageHeaderText>

            {/* O badge de status não vive aqui (decisão 2026-08-22): sobre o
                fundo escuro do shell, o verde/vermelho translúcido perdia
                contraste. Ele fica no cabeçalho do card "Dados do movimento". */}
            <PageHeaderActions>
               <TextLink as="button" type="button" onClick={handleBack}>
                  ← Voltar
               </TextLink>
            </PageHeaderActions>
         </PageHeader>

         {!!transferBinding && (
            <TransferNote>
               <span className="ic" aria-hidden="true">
                  <FaExchangeAlt size={14} />
               </span>
               <span>
                  Movimento gerado pela{' '}
                  <strong>
                     transferência #{transferBinding.transfer_order_id}
                  </strong>{' '}
                  (remessa #{transferBinding.transfer_shipment_id} —{' '}
                  {TRANSFER_ROLE_LABEL[transferBinding.role] ??
                     transferBinding.role}
                  ). Editar e excluir estão indisponíveis aqui: a mutação
                  acontece pelo fluxo de transferência, que mantém o par de
                  movimentos e os acumuladores da ordem consistentes.
               </span>
               {showTransferShortcut && (
                  <span className="act">
                     <SmallButton
                        type="button"
                        $intent="cta"
                        onClick={() =>
                           navigate(
                              `/inventory/transfers/${transferBinding.transfer_order_id}`
                           )
                        }
                        title="Abrir a transferência que gerou este movimento"
                     >
                        Ver #{transferBinding.transfer_order_id}
                     </SmallButton>
                  </span>
               )}
            </TransferNote>
         )}

         {/* A barra aparece em movimento confirmado E em excluído (decisão do
             Caio, 2026-09-12) — no excluído, SÓ com o Imprimir: o comprovante
             de um movimento anulado serve conferência e sai marcado como tal.
             Excluir e Editar seguem exclusivos de `confirmed`. */}
         {!!movement && (isConfirmed || isDeleted) && (
            <EntityActionBar>
               {/* Movimento de transferência não se exclui por aqui (409
                   `transfer_movement_locked`) — o botão nem aparece. */}
               {isConfirmed && !transferBinding && (
                  <>
                     <DangerButton
                        type="button"
                        onClick={() => setIsDeleteModalOpen(true)}
                        disabled={isLoading}
                     >
                        Excluir movimento
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               {/* Gate de UI novo: com a impressão no cliente, a chave
                   `inventory:movement:print` deixou de ser exercida pelo
                   endpoint e passa a valer aqui (e na rota do comprovante). */}
               {hasPermission('inventory:movement:print') && (
                  <CtaButton
                     type="button"
                     onClick={handlePrintMovement}
                     disabled={isLoading}
                  >
                     <FaPrint size={11} aria-hidden="true" /> Imprimir
                  </CtaButton>
               )}
               {/* XML é imutável (0093 §3: exclua e reimporte o corrigido) —
                   sem botão Editar quando há doc_key. Transferência idem. */}
               {isConfirmed &&
                  hasPermission('inventory:movement:update') &&
                  !movement.doc_key &&
                  !transferBinding && (
                     <CtaButton
                        type="button"
                        onClick={() => navigate(`/movements/${id}/edit`)}
                        disabled={isLoading}
                     >
                        Editar
                     </CtaButton>
                  )}
            </EntityActionBar>
         )}

         {!movement && !isLoading ? (
            <Section>
               <SectionBody>
                  <EmptyState
                     title={
                        loadFailed
                           ? 'Não foi possível abrir o movimento'
                           : 'Movimento não encontrado'
                     }
                     description="Verifique o ID e tente novamente."
                     action={
                        <TextLink as="button" type="button" onClick={handleBack}>
                           Voltar para a listagem
                        </TextLink>
                     }
                  />
               </SectionBody>
            </Section>
         ) : (
            <>
               <Section>
                  <SectionHeader>
                     <SectionTitle>Dados do movimento</SectionTitle>
                     <SectionActions>
                        <HeadPills>
                           {!!movement?.move_type && (
                              <StatusBadge
                                 variant={moveTypeVariant(movement.move_type)}
                              >
                                 {moveTypeLabel(movement.move_type)}
                              </StatusBadge>
                           )}
                           {!!movement?.movement_flag && (
                              <StatusBadge
                                 variant={movementFlagVariant(
                                    movement.movement_flag
                                 )}
                              >
                                 {movementFlagLabel(movement.movement_flag)}
                              </StatusBadge>
                           )}
                        </HeadPills>
                     </SectionActions>
                  </SectionHeader>

                  <SectionBody>
                     <DataGrid>
                        <DataItem>
                           <dt>ID do movimento</dt>
                           <dd className="strong">#{movement?.id ?? id}</dd>
                        </DataItem>

                        <DataItem>
                           <dt>PDV</dt>
                           <dd>{movementPdvLabel(movement) || '—'}</dd>
                        </DataItem>

                        <DataItem>
                           <dt>Usuário</dt>
                           <dd>{movementUserLabel(movement) || '—'}</dd>
                        </DataItem>

                        <DataItem>
                           <dt>Data de registro</dt>
                           <dd>
                              {formatMovementDateTime(movement?.created_at) ||
                                 '—'}
                           </dd>
                        </DataItem>

                        <DataItem>
                           <dt>Documento</dt>
                           <dd>{movement?.doc_type || '—'}</dd>
                        </DataItem>

                        <DataItem>
                           <dt>Nº do documento</dt>
                           <dd>{movement?.doc_number ?? '—'}</dd>
                        </DataItem>

                        {!!movement?.doc_key && (
                           <DataItem $span={2}>
                              <dt>Chave de acesso (XML)</dt>
                              <dd>{movement.doc_key}</dd>
                           </DataItem>
                        )}

                        <DataItem $span={2}>
                           <dt>Descrição</dt>
                           <dd>{movement?.movement_description || '—'}</dd>
                        </DataItem>

                        {/* Dados de cancelamento (handoff 0095) — só em
                            movimento excluído */}
                        {isDeleted && (
                           <>
                              <DataItem $span={2}>
                                 <dt>Motivo da exclusão</dt>
                                 <dd>{movement?.cancelled_reason || '—'}</dd>
                              </DataItem>

                              <DataItem>
                                 <dt>Excluído em</dt>
                                 <dd>
                                    {formatMovementDateTime(
                                       movement?.cancelled_at
                                    ) || '—'}
                                 </dd>
                              </DataItem>

                              <DataItem>
                                 <dt>Excluído por</dt>
                                 <dd>
                                    {[
                                       movement?.cancelled_by,
                                       movement?.cancelled_by_name,
                                    ]
                                       .filter(Boolean)
                                       .join(' - ') || '—'}
                                 </dd>
                              </DataItem>
                           </>
                        )}
                     </DataGrid>
                  </SectionBody>
               </Section>

               <Section>
                  <SectionHeader>
                     <SectionTitle>Itens do movimento</SectionTitle>
                     <SectionActions>
                        {items.length
                           ? `${items.length} ${
                                items.length === 1 ? 'item' : 'itens'
                             } · ${totalUnits} un.`
                           : ''}
                     </SectionActions>
                  </SectionHeader>

                  <SectionBody>
                     {items.length ? (
                        <TableWrapper $minWidth="0">
                           <DataTable>
                              <thead>
                                 <tr>
                                    <th className="center">SKU</th>
                                    <th>Nome do produto</th>
                                    <th className="num">Qtd.</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {items.map((item, idx) => {
                                    const product = productDetails[idx];
                                    const sku =
                                       product?.sku ?? product?.prod_sku ?? '';
                                    const name =
                                       product?.name ?? product?.prod_name ?? '';

                                    return (
                                       <tr
                                          key={String(
                                             item?.id ??
                                                `${item?.prod_id}-${idx}`
                                          )}
                                       >
                                          <td className="center">
                                             {sku || item?.prod_id || '—'}
                                          </td>
                                          <td>
                                             {name || `Produto ${item?.prod_id}`}
                                          </td>
                                          <td className="num">
                                             {item?.prod_qtd ?? '—'}
                                          </td>
                                       </tr>
                                    );
                                 })}
                              </tbody>
                              <tfoot>
                                 <TotalRow>
                                    <td colSpan={2}>
                                       Total · {items.length}{' '}
                                       {items.length === 1 ? 'item' : 'itens'}
                                    </td>
                                    <td className="num">{totalUnits} un.</td>
                                 </TotalRow>
                              </tfoot>
                           </DataTable>
                        </TableWrapper>
                     ) : (
                        <EmptyState
                           title="Sem itens"
                           description="Este movimento não possui itens associados."
                        />
                     )}
                  </SectionBody>
               </Section>
            </>
         )}

         <DeleteMovementModal
            isOpen={isDeleteModalOpen}
            movementId={movement?.id ?? id}
            isSubmitting={isLoading}
            onCancel={() => setIsDeleteModalOpen(false)}
            onConfirm={handleDeleteMovement}
         />
      </Container>
   );
}
